import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../utils/axios';
import {
  ChartBarIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { LoadingSpinner, EmptyState, AccessibleButton } from '../../components/ui';

const AgentTargetsPage = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchTargets();
  }, [showHistory]);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const endpoint = showHistory ? 
        '/api/field-agent/my/targets/history' : 
        '/api/field-agent/my/targets';
      
      const response = await axios.get(endpoint);
      setTargets(response.data.targets || []);
    } catch (error) {
      console.error('Error fetching targets:', error);
      toast.error('שגיאה בטעינת היעדים');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'תאריך לא זמין';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'שגיאה בתאריך';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { text: 'פעיל', className: 'bg-blue-100 text-blue-800' },
      completed: { text: 'הושלם', className: 'bg-green-100 text-green-800' },
      failed: { text: 'נכשל', className: 'bg-red-100 text-red-800' },
      cancelled: { text: 'בוטל', className: 'bg-gray-100 text-gray-800' }
    };

    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const calculateProgressPercentage = (target) => {
    if (!target.progress) return 0;
    
    if (target.target_type === 'revenue') {
      return Math.round((target.progress.current_revenue / target.target_revenue) * 100);
    } else if (target.target_type === 'orders') {
      return Math.round((target.progress.current_orders / target.target_orders) * 100);
    }
    return 0;
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!targets || targets.length === 0) return null;

    // Bar chart data for comparing targets
    const barChartData = targets.map((target) => {
      const progress = calculateProgressPercentage(target);
      const isRevenue = target.target_type === 'revenue';
      const current = isRevenue 
        ? target.progress?.current_revenue || 0
        : target.progress?.current_orders || 0;
      const targetValue = isRevenue 
        ? target.target_revenue || 0
        : target.target_orders || 0;

      return {
        name: target.description || (isRevenue ? 'יעד הכנסות' : 'יעד הזמנות'),
        current: isRevenue ? current / 1000 : current, // Convert to thousands for revenue
        target: isRevenue ? targetValue / 1000 : targetValue,
        progress: progress,
        type: isRevenue ? 'הכנסות (אלפים)' : 'הזמנות'
      };
    });

    // Summary metrics
    const totalTargets = targets.length;
    const completedTargets = targets.filter(t => calculateProgressPercentage(t) >= 100).length;
    const activeTargets = targets.filter(t => t.status === 'active').length;
    const totalProgress = targets.reduce((sum, t) => sum + calculateProgressPercentage(t), 0) / totalTargets || 0;

    return {
      barChartData,
      summary: {
        totalTargets,
        completedTargets,
        activeTargets,
        averageProgress: Math.round(totalProgress)
      }
    };
  }, [targets]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString('he-IL')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format currency for tooltips
  const formatCurrencyTooltip = (value) => {
    return `₪${value.toLocaleString('he-IL')}`;
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">היעדים שלי</h1>
          <p className="mt-2 text-sm text-gray-600">
            מעקב אחר יעדי המכירות שלך
          </p>
        </div>
        <AccessibleButton
          onClick={() => setShowHistory(!showHistory)}
          variant="outline"
          size="md"
          ariaLabel={showHistory ? 'הצג יעדים פעילים' : 'הצג היסטוריה'}
        >
          {showHistory ? 'הצג יעדים פעילים' : 'הצג היסטוריה'}
        </AccessibleButton>
      </div>

      {/* Summary Cards */}
      {chartData && chartData.summary && targets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">סה"כ יעדים</p>
                <p className="text-3xl font-bold">{chartData.summary.totalTargets}</p>
              </div>
              <ChartBarIcon className="h-10 w-10 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">יעדים הושלמו</p>
                <p className="text-3xl font-bold">{chartData.summary.completedTargets}</p>
              </div>
              <CheckCircleIcon className="h-10 w-10 text-green-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium mb-1">יעדים פעילים</p>
                <p className="text-3xl font-bold">{chartData.summary.activeTargets}</p>
              </div>
              <ClockIcon className="h-10 w-10 text-yellow-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium mb-1">ממוצע התקדמות</p>
                <p className="text-3xl font-bold">{chartData.summary.averageProgress}%</p>
              </div>
              <ArrowTrendingUpIcon className="h-10 w-10 text-purple-200" />
            </div>
          </div>
        </div>
      )}

      {/* Targets List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" color="primary" text="טוען יעדים..." />
        </div>
      ) : targets.length === 0 ? (
        <div className="bg-white shadow-lg rounded-xl border border-gray-100">
          <EmptyState
            type="generic"
            icon={ChartBarIcon}
            title={showHistory ? 'אין היסטוריית יעדים' : 'אין יעדים פעילים'}
            description={showHistory ? 'אין היסטוריית יעדים להצגה' : 'אין יעדים פעילים כרגע'}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {targets.map((target) => {
            const progressPercentage = calculateProgressPercentage(target);
            const isRevenue = target.target_type === 'revenue';
            const isOrders = target.target_type === 'orders';
            
            // Prepare donut chart data
            const currentValue = isRevenue 
              ? target.progress?.current_revenue || 0
              : target.progress?.current_orders || 0;
            const targetValue = isRevenue 
              ? target.target_revenue || 0
              : target.target_orders || 0;
            const remaining = Math.max(0, targetValue - currentValue);
            
            const donutData = [
              { name: 'הושג', value: currentValue },
              { name: 'נותר', value: remaining }
            ];

            return (
              <div key={target.id} className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="p-4 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {getStatusIcon(target.status)}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {target.description || (isRevenue ? 'יעד הכנסות' : 'יעד הזמנות')}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 ml-1 flex-shrink-0" />
                            <span className="truncate">{formatDate(target.period_start)} - {formatDate(target.period_end)}</span>
                          </span>
                          {target.days_remaining !== undefined && target.status === 'active' && (
                            <span className="text-orange-600 font-medium whitespace-nowrap">
                              {target.days_remaining} ימים נותרו
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(target.status)}
                  </div>

                  {/* Progress Section with Donut Chart */}
                  {target.progress && (
                    <div className="space-y-4">
                      {/* Donut Chart and Progress Bar */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        {/* Donut Chart */}
                        <div className="flex justify-center relative">
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie
                                data={donutData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={70}
                                paddingAngle={2}
                                dataKey="value"
                                startAngle={90}
                                endAngle={-270}
                              >
                                {donutData.map((entry, index) => {
                                  // Get color from progress percentage
                                  let color = '#E5E7EB';
                                  if (index === 0) {
                                    if (progressPercentage >= 100) color = '#10B981';
                                    else if (progressPercentage >= 75) color = '#3B82F6';
                                    else if (progressPercentage >= 50) color = '#F59E0B';
                                    else color = '#EF4444';
                                  }
                                  return (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={color}
                                    />
                                  );
                                })}
                              </Pie>
                              <Tooltip 
                                formatter={(value) => isRevenue ? formatCurrency(value) : value.toLocaleString('he-IL')}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-900">{progressPercentage}%</div>
                              <div className="text-xs text-gray-500">התקדמות</div>
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="flex flex-col justify-center">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">התקדמות</span>
                            <span className="text-lg font-bold text-gray-900">
                              {progressPercentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ease-out ${getProgressColor(progressPercentage)}`}
                              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            />
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {isRevenue 
                              ? `${formatCurrency(currentValue)} / ${formatCurrency(targetValue)}`
                              : `${currentValue.toLocaleString('he-IL')} / ${targetValue.toLocaleString('he-IL')}`
                            }
                          </div>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-3">
                        {/* Revenue Stats */}
                        {isRevenue && (
                          <>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                              <div className="flex items-center gap-2 mb-1">
                                <CurrencyDollarIcon className="h-4 w-4 text-blue-600" />
                                <p className="text-xs text-blue-700 font-medium">יעד</p>
                              </div>
                              <p className="text-base font-bold text-gray-900">
                                {formatCurrency(target.target_revenue)}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                              <div className="flex items-center gap-2 mb-1">
                                <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                <p className="text-xs text-green-700 font-medium">נוכחי</p>
                              </div>
                              <p className="text-base font-bold text-gray-900">
                                {formatCurrency(target.progress.current_revenue)}
                              </p>
                            </div>
                            <div className={`rounded-lg p-3 border ${
                              target.progress.current_revenue >= target.target_revenue
                                ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                                : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <ClockIcon className={`h-4 w-4 ${
                                  target.progress.current_revenue >= target.target_revenue
                                    ? 'text-green-600'
                                    : 'text-orange-600'
                                }`} />
                                <p className={`text-xs font-medium ${
                                  target.progress.current_revenue >= target.target_revenue
                                    ? 'text-green-700'
                                    : 'text-orange-700'
                                }`}>נותר</p>
                              </div>
                              <p className={`text-base font-bold ${
                                target.progress.current_revenue >= target.target_revenue
                                  ? 'text-green-700'
                                  : 'text-orange-700'
                              }`}>
                                {formatCurrency(Math.max(0, target.target_revenue - target.progress.current_revenue))}
                              </p>
                            </div>
                          </>
                        )}

                        {/* Orders Stats */}
                        {isOrders && (
                          <>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                              <div className="flex items-center gap-2 mb-1">
                                <ShoppingBagIcon className="h-4 w-4 text-blue-600" />
                                <p className="text-xs text-blue-700 font-medium">יעד</p>
                              </div>
                              <p className="text-base font-bold text-gray-900">
                                {target.target_orders.toLocaleString('he-IL')}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                              <div className="flex items-center gap-2 mb-1">
                                <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                <p className="text-xs text-green-700 font-medium">נוכחי</p>
                              </div>
                              <p className="text-base font-bold text-gray-900">
                                {target.progress.current_orders.toLocaleString('he-IL')}
                              </p>
                            </div>
                            <div className={`rounded-lg p-3 border ${
                              target.progress.current_orders >= target.target_orders
                                ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                                : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <ClockIcon className={`h-4 w-4 ${
                                  target.progress.current_orders >= target.target_orders
                                    ? 'text-green-600'
                                    : 'text-orange-600'
                                }`} />
                                <p className={`text-xs font-medium ${
                                  target.progress.current_orders >= target.target_orders
                                    ? 'text-green-700'
                                    : 'text-orange-700'
                                }`}>נותר</p>
                              </div>
                              <p className={`text-base font-bold ${
                                target.progress.current_orders >= target.target_orders
                                  ? 'text-green-700'
                                  : 'text-orange-700'
                              }`}>
                                {Math.max(0, target.target_orders - target.progress.current_orders).toLocaleString('he-IL')}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Achievement Message */}
                      {progressPercentage >= 100 && target.status === 'active' && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800">
                            כל הכבוד! השגת את היעד! 🎉
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {target.notes && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-900">
                        <span className="font-medium">הערות: </span>
                        {target.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentTargetsPage;

